import { Link, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useLotteryEnabled } from '@/shared/hooks/useLotteryEnabled'
import { portalNavAriaCurrentPage } from '@/shared/lib/portalNavAriaCurrent'
import { cn } from '@/shared/lib/utils'
import { HORIZON_ICON_REGISTRY } from '../../icons'
import type { IconKey, ThemedIconComponent } from '@/shared/theme/resolveThemedIcon'

type BottomNavIconKey = Extract<IconKey, 'home' | 'reservation' | 'ticket' | 'gift' | 'user'>

interface BottomNavItem {
  to: string
  labelKey: string
  icon: BottomNavIconKey
}

const ITEMS: BottomNavItem[] = [
  { to: '/', labelKey: 'app.layout.nav_home', icon: 'home' },
  { to: '/reservation/properties', labelKey: 'app.layout.nav_properties', icon: 'reservation' },
  { to: '/ticketing', labelKey: 'app.layout.nav_ticketing', icon: 'ticket' },
  { to: '/lottery', labelKey: 'app.layout.nav_lottery', icon: 'gift' },
  { to: '/me', labelKey: 'app.layout.nav_profile', icon: 'user' },
]

export function BottomNav() {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const lotteryEnabled = useLotteryEnabled()
  const items = ITEMS.filter((item) => item.to !== '/lottery' || lotteryEnabled)
  const iconByKey = {
    home: HORIZON_ICON_REGISTRY.home,
    reservation: HORIZON_ICON_REGISTRY.reservation,
    ticket: HORIZON_ICON_REGISTRY.ticket,
    gift: HORIZON_ICON_REGISTRY.gift,
    user: HORIZON_ICON_REGISTRY.user,
  } satisfies Record<BottomNavItem['icon'], ThemedIconComponent>

  return (
    <nav
      aria-label={t('app.layout.bottom_nav')}
      className="fixed inset-x-0 bottom-0 z-[var(--brand-z-header)] border-t border-[var(--brand-border-soft)] bg-[var(--brand-surface)] pb-[env(safe-area-inset-bottom)] shadow-[var(--brand-shadow-lift)] lg:hidden"
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around">
        {items.map((item) => {
          const active = portalNavAriaCurrentPage(pathname, item.to)
          const Icon = iconByKey[item.icon]
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-[var(--brand-radius-md)] px-1 py-1.5 text-[11px] font-semibold no-underline outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]',
                  active
                    ? 'text-[var(--brand-primary-strong)]'
                    : 'text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)]',
                )}
              >
                <Icon className="size-5 shrink-0" aria-hidden />
                <span className="truncate">{t(item.labelKey)}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
